import React, { useCallback, useEffect, useRef, useState } from "react";
import Popover from "@material-ui/core/Popover";
import { makeStyles } from "@material-ui/core";
import cn from "classnames";
import CustomColorPicker from "./CustomColorPicker";
import { MinusIcon } from "@radix-ui/react-icons";
import { ToggleEyesIcon } from "./SidebarBottomTool";
import { useColorPickerContext } from "./ColorPickerContext";
import { getOpacityPercentage, setOpacityPercentage } from "../../utils";
import { useDragToAdjust } from "../hooks/useDragToAdjust";

const useStyles = makeStyles(() => ({
  paper: {
    transform: "translateX(-284px) !important",
  },
}));

interface QrColorPickerProps {
  pixelColor: string;
  backgroundColor: string;
  onPixelColorChange: (color: string) => void;
  onBackgroundColorChange: (color: string) => void;
  showPixelColor?: boolean;
  showBackgroundColor?: boolean;
  onRemoveBackgroundColor?: () => void;
  isBackgroundColorVisible?: boolean;
  onBackgroundColorVisibilityToggle?: () => void;
}

const QrColorPicker: React.FC<QrColorPickerProps> = ({
  pixelColor,
  backgroundColor,
  onPixelColorChange,
  onBackgroundColorChange,
  showPixelColor = true,
  showBackgroundColor = false,
  onRemoveBackgroundColor,
  isBackgroundColorVisible = true,
  onBackgroundColorVisibilityToggle,
}) => {
  const classes = useStyles();

  // Ref to track if we're actively changing color (to prevent external overwrite)
  const isChangingColorRef = useRef(false);
  const colorChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for immediate UI updates
  const [localPixelColor, setLocalPixelColor] = useState(pixelColor?.toLowerCase() || "#000000");
  const [localBackgroundColor, setLocalBackgroundColor] = useState(backgroundColor?.toLowerCase() || "");

  // State for editing opacity
  const [isEditingPixelOpacity, setIsEditingPixelOpacity] = useState(false);
  const [isEditingBackgroundOpacity, setIsEditingBackgroundOpacity] = useState(false);
  const [pixelOpacityInput, setPixelOpacityInput] = useState("");
  const [backgroundOpacityInput, setBackgroundOpacityInput] = useState("");
  const pixelOpacityInputRef = useRef<HTMLInputElement | null>(null);
  const backgroundOpacityInputRef = useRef<HTMLInputElement | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (colorChangeTimeoutRef.current) {
        clearTimeout(colorChangeTimeoutRef.current);
      }
    };
  }, []);

  // Sync local state when props change from outside (only if significantly different and not actively changing)
  useEffect(() => {
    // Don't sync if we're actively changing the color
    if (isChangingColorRef.current) {
      return;
    }

    const normalizedProp = pixelColor?.toLowerCase() || "#000000";
    setLocalPixelColor((prevLocal) => {
      const normalizedLocal = prevLocal?.toLowerCase() || "#000000";
      if (normalizedProp !== normalizedLocal) {
        return normalizedProp;
      }
      return prevLocal;
    });
  }, [pixelColor]);

  useEffect(() => {
    // Don't sync if we're actively changing the color
    if (isChangingColorRef.current) {
      return;
    }

    const normalizedProp = backgroundColor?.toLowerCase() || "";
    setLocalBackgroundColor((prevLocal) => {
      const normalizedLocal = prevLocal?.toLowerCase() || "";
      if (normalizedProp !== normalizedLocal) {
        return normalizedProp;
      }
      return prevLocal;
    });
  }, [backgroundColor]);

  // Use color picker context for global state management
  const { openColorPicker, closeColorPicker, isOpen } = useColorPickerContext();
  const pixelPickerId = "qr-pixel-color-picker";
  const backgroundPickerId = "qr-background-color-picker";

  // Pixel color picker state
  const [pixelAnchorEl, setPixelAnchorEl] = useState<HTMLButtonElement | null>(null);
  const pixelOpen = Boolean(pixelAnchorEl) && isOpen(pixelPickerId);

  // Background color picker state
  const [backgroundAnchorEl, setBackgroundAnchorEl] = useState<HTMLButtonElement | null>(null);
  const backgroundOpen = Boolean(backgroundAnchorEl) && isOpen(backgroundPickerId);

  const handlePixelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPixelAnchorEl(event.currentTarget);
    openColorPicker(pixelPickerId);
  };

  const handlePixelClose = () => {
    setPixelAnchorEl(null);
    closeColorPicker(pixelPickerId);
  };

  const handleBackgroundClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBackgroundAnchorEl(event.currentTarget);
    openColorPicker(backgroundPickerId);
  };

  const handleBackgroundClose = () => {
    setBackgroundAnchorEl(null);
    closeColorPicker(backgroundPickerId);
  };

  // Close pickers if another one opens
  React.useEffect(() => {
    if (!isOpen(pixelPickerId) && pixelAnchorEl) {
      setPixelAnchorEl(null);
    }
  }, [isOpen, pixelAnchorEl]);

  React.useEffect(() => {
    if (!isOpen(backgroundPickerId) && backgroundAnchorEl) {
      setBackgroundAnchorEl(null);
    }
  }, [isOpen, backgroundAnchorEl]);

  const handlePixelColorChange = (color: string) => {
    // Mark that we're actively changing color
    isChangingColorRef.current = true;

    // Clear any pending timeout
    if (colorChangeTimeoutRef.current) {
      clearTimeout(colorChangeTimeoutRef.current);
    }

    // Normalize color to lowercase for consistency
    const normalizedColor = color?.toLowerCase() || "#000000";
    // Update local state immediately for instant UI feedback
    setLocalPixelColor(normalizedColor);
    // Notify parent
    onPixelColorChange(normalizedColor);

    // Clear the flag after async operations complete
    colorChangeTimeoutRef.current = setTimeout(() => {
      isChangingColorRef.current = false;
    }, 200);
  };

  const handleBackgroundColorChange = (color: string) => {
    // Mark that we're actively changing color
    isChangingColorRef.current = true;

    // Clear any pending timeout
    if (colorChangeTimeoutRef.current) {
      clearTimeout(colorChangeTimeoutRef.current);
    }

    // Normalize color to lowercase for consistency
    const normalizedColor = color?.toLowerCase() || "";
    // Update local state immediately for instant UI feedback
    setLocalBackgroundColor(normalizedColor);
    // Notify parent
    onBackgroundColorChange(normalizedColor);

    // Clear the flag after async operations complete
    colorChangeTimeoutRef.current = setTimeout(() => {
      isChangingColorRef.current = false;
    }, 200);
  };

  const handlePixelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalPixelColor(value);
    if (/^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(value)) {
      handlePixelColorChange(value);
    }
  };

  const handlePixelInputBlur = () => {
    if (!/^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(localPixelColor)) {
      setLocalPixelColor(pixelColor?.toLowerCase() || "#000000");
    }
  };

  const handleBackgroundInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalBackgroundColor(value);
    if (/^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(value)) {
      handleBackgroundColorChange(value);
    } else if (value === "") {
      handleBackgroundColorChange("");
    }
  };

  const handleBackgroundInputBlur = () => {
    if (localBackgroundColor !== "" && !/^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(localBackgroundColor)) {
      setLocalBackgroundColor(backgroundColor?.toLowerCase() || "");
    }
  };

  // Handle pixel opacity change
  const handlePixelOpacityChange = (opacityValue: number) => {
    const newColor = setOpacityPercentage(localPixelColor, opacityValue);
    handlePixelColorChange(newColor);
  };

  // Handle background opacity change
  const handleBackgroundOpacityChange = (opacityValue: number) => {
    const newColor = setOpacityPercentage(localBackgroundColor || "#ffffff", opacityValue);
    handleBackgroundColorChange(newColor);
  };

  // Drag-to-adjust on opacity %
  const pixelOpacityRef = useRef(0);
  pixelOpacityRef.current = getOpacityPercentage(localPixelColor);
  const pixelOpacityDrag = useDragToAdjust(
    () => pixelOpacityRef.current,
    (next) => handlePixelOpacityChange(Math.max(0, Math.min(100, next))),
    {
      min: 0,
      max: 100,
      step: 1,
      onClick: () => {
        setPixelOpacityInput(getOpacityPercentage(localPixelColor).toString());
        setIsEditingPixelOpacity(true);
        setTimeout(() => {
          pixelOpacityInputRef.current?.focus();
          pixelOpacityInputRef.current?.select();
        }, 0);
      },
    }
  );

  const backgroundOpacityRef = useRef(0);
  backgroundOpacityRef.current = getOpacityPercentage(localBackgroundColor || "#ffffff");
  const backgroundOpacityDrag = useDragToAdjust(
    () => backgroundOpacityRef.current,
    (next) => handleBackgroundOpacityChange(Math.max(0, Math.min(100, next))),
    {
      min: 0,
      max: 100,
      step: 1,
      onClick: () => {
        setBackgroundOpacityInput(getOpacityPercentage(localBackgroundColor || "#ffffff").toString());
        setIsEditingBackgroundOpacity(true);
        setTimeout(() => {
          backgroundOpacityInputRef.current?.focus();
          backgroundOpacityInputRef.current?.select();
        }, 0);
      },
    }
  );

  return (
    <div className="flex flex-col gap-2">
      {/* QR Pixel Color - Only shown when explicitly enabled */}
      {showPixelColor && (
        <div
          className="flex items-center w-full py-1 px-2 rounded-[4px] relative"
          style={{ border: "1px solid #E2E2FC", marginTop: "4px" }}
        >
          <button
            className="min-w-4 min-h-4 rounded-[4px] mr-[6px] color-picker-border"
            style={{
              backgroundColor: localPixelColor,
            }}
            onClick={handlePixelClick}
          />
          <input
            type="text"
            value={localPixelColor.toUpperCase()}
            onChange={handlePixelInputChange}
            onBlur={handlePixelInputBlur}
            className="h-6 text-[10px] text-[#2E3860] rounded-[4px] w-[55px] px-1 flex items-center justify-center text-left mr-1"
          />

          {/* Separator at 3/4 from left */}
          <div
            className="absolute h-4 bg-[#E2E2FC]"
            style={{
              left: "75%",
              width: "1px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />

          {/* Opacity percentage near right edge - editable */}
          {isEditingPixelOpacity ? (
            <input
              ref={pixelOpacityInputRef}
              type="text"
              value={pixelOpacityInput}
              onChange={(e) => {
                const value = e.target.value.replace("%", "").trim();
                if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100)) {
                  setPixelOpacityInput(value);
                }
              }}
              onBlur={() => {
                const opacityValue =
                  pixelOpacityInput === "" ? 100 : Math.min(100, Math.max(0, Number(pixelOpacityInput)));
                handlePixelOpacityChange(opacityValue);
                setIsEditingPixelOpacity(false);
                setPixelOpacityInput("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  setIsEditingPixelOpacity(false);
                  setPixelOpacityInput("");
                }
              }}
              className="ml-auto text-[10px] text-[#2E3860] pr-1 w-10 text-right bg-transparent border-none outline-none focus:outline-none"
              autoFocus
            />
          ) : (
            <span
              className="ml-auto text-[10px] text-[#2E3860] pr-1 hover:opacity-70 select-none"
              style={{ cursor: "ew-resize" }}
              title="Drag to adjust opacity, click to type"
              onMouseDown={pixelOpacityDrag.onMouseDown}
            >
              {getOpacityPercentage(localPixelColor)}%
            </span>
          )}

          <Popover
            id={`pixel-color-popover`}
            open={pixelOpen}
            anchorEl={pixelAnchorEl}
            onClose={handlePixelClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            classes={{ paper: classes.paper }}
            style={{ transform: "translateX(8px)" }}
            PaperProps={{ style: { pointerEvents: "auto", transform: "translateX(8px)", borderRadius: "8px" } }}
          >
            <div className="p-4 rounded-[8px] w-[274px]">
              <CustomColorPicker
                editor={{} as any}
                colorProp="qrPixelColor"
                shapeColor={localPixelColor}
                onColorChange={handlePixelColorChange}
              />
            </div>
          </Popover>
        </div>
      )}

      {/* Background Color - Only shown when explicitly enabled */}
      {showBackgroundColor && (
        <div
          className="flex items-center w-full py-1 px-2 rounded-[4px] relative"
          style={{ border: "1px solid #E2E2FC", marginTop: "4px" }}
        >
          <button
            className="min-w-4 min-h-4 rounded-[4px] mr-[6px] color-picker-border"
            style={{
              backgroundColor: localBackgroundColor || "#f3f4f6",
            }}
            onClick={handleBackgroundClick}
          />
          <input
            type="text"
            value={(localBackgroundColor || "").toUpperCase()}
            onChange={handleBackgroundInputChange}
            onBlur={handleBackgroundInputBlur}
            className="h-6 text-[10px] text-[#2E3860] rounded-[4px] w-[55px] px-1 flex items-center justify-center text-left mr-1"
            placeholder="Transparent"
          />
          <div className="flex items-center gap-1">
            {onBackgroundColorVisibilityToggle && (
              <ToggleEyesIcon isOpen={isBackgroundColorVisible} onClick={onBackgroundColorVisibilityToggle} />
            )}
            {onRemoveBackgroundColor && (
              <MinusIcon className="h-5 w-5 cursor-pointer" onClick={onRemoveBackgroundColor} />
            )}
          </div>

          {/* Separator at 3/4 from left */}
          <div
            className="absolute h-4 bg-[#E2E2FC]"
            style={{
              left: "75%",
              width: "1px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />

          {/* Opacity percentage near right edge - editable */}
          {isEditingBackgroundOpacity ? (
            <input
              ref={backgroundOpacityInputRef}
              type="text"
              value={backgroundOpacityInput}
              onChange={(e) => {
                const value = e.target.value.replace("%", "").trim();
                if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100)) {
                  setBackgroundOpacityInput(value);
                }
              }}
              onBlur={() => {
                const opacityValue =
                  backgroundOpacityInput === "" ? 100 : Math.min(100, Math.max(0, Number(backgroundOpacityInput)));
                handleBackgroundOpacityChange(opacityValue);
                setIsEditingBackgroundOpacity(false);
                setBackgroundOpacityInput("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  setIsEditingBackgroundOpacity(false);
                  setBackgroundOpacityInput("");
                }
              }}
              className="ml-auto text-[10px] text-[#2E3860] pr-1 w-10 text-right bg-transparent border-none outline-none focus:outline-none"
              autoFocus
            />
          ) : (
            <span
              className="ml-auto text-[10px] text-[#2E3860] pr-1 hover:opacity-70 select-none"
              style={{ cursor: "ew-resize" }}
              title="Drag to adjust opacity, click to type"
              onMouseDown={backgroundOpacityDrag.onMouseDown}
            >
              {getOpacityPercentage(localBackgroundColor || "#ffffff")}%
            </span>
          )}

          <Popover
            id={`background-color-popover`}
            open={backgroundOpen}
            anchorEl={backgroundAnchorEl}
            onClose={handleBackgroundClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            classes={{ paper: classes.paper }}
            style={{ transform: "translateX(8px)" }}
            PaperProps={{ style: { pointerEvents: "auto", transform: "translateX(8px)", borderRadius: "8px" } }}
          >
            <div className="p-4 rounded-[8px] w-[274px]">
              <CustomColorPicker
                editor={{} as any}
                colorProp="qrBackgroundColor"
                shapeColor={localBackgroundColor || "#ffffff"}
                onColorChange={handleBackgroundColorChange}
              />
            </div>
          </Popover>
        </div>
      )}
    </div>
  );
};

export default QrColorPicker;

// Custom CSS for color picker borders
const colorPickerBorderStyle = `
  .color-picker-border {
    border: 1px solid #E1E6EB !important;
    box-sizing: border-box !important;
  }
`;

// Inject the style into the document head
if (typeof document !== "undefined") {
  const styleId = "color-picker-border-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = colorPickerBorderStyle;
    document.head.appendChild(style);
  }
}
